import type { Rule } from 'ant-design-vue/es/form'
import type { ZodError, ZodTypeAny } from 'zod'

const DEFAULT_TRIGGER: Rule['trigger'] = ['change', 'blur']

/** 单字段 AntDV Rule：底层走 Zod safeParse */
export function zodFieldRule(
  fieldSchema: ZodTypeAny,
  trigger: Rule['trigger'] = DEFAULT_TRIGGER,
  required = false,
): Rule {
  return {
    // 与 Form.Item :required 对齐：阻止 AntDV 再拼一条默认「请输入xxx」
    ...(required ? { required: true } : {}),
    trigger,
    validator: async (_rule, value) => {
      const result = fieldSchema.safeParse(value)
      if (!result.success) {
        const message = result.error.issues[0]?.message ?? '校验失败'
        return Promise.reject(new Error(message))
      }
      return Promise.resolve()
    },
  }
}

/** Zod 整表错误 → formRef.setFields 结构 */
export function mapZodErrorToFormFields(error: ZodError): { name: string; errors: string[] }[] {
  const map = new Map<string, string[]>()
  for (const issue of error.issues) {
    const name = String(issue.path[0] ?? '')
    if (!name) continue
    const list = map.get(name) ?? []
    list.push(issue.message)
    map.set(name, list)
  }
  return [...map.entries()].map(([name, errors]) => ({ name, errors }))
}

/** 422 响应体字段错误 → setFields（兼容 errors 对象或 fieldErrors 数组） */
export function mapApiFieldErrors(payload: unknown): { name: string; errors: string[] }[] {
  if (!payload || typeof payload !== 'object') return []
  const o = payload as Record<string, unknown>

  if (o.errors && typeof o.errors === 'object' && !Array.isArray(o.errors)) {
    return Object.entries(o.errors as Record<string, string | string[]>).map(([name, msg]) => ({
      name,
      errors: Array.isArray(msg) ? msg.map(String) : [String(msg)],
    }))
  }

  if (Array.isArray(o.fieldErrors)) {
    return (o.fieldErrors as { field?: string; message?: string }[])
      .filter((item) => item.field)
      .map((item) => ({
        name: String(item.field),
        errors: [String(item.message ?? '校验失败')],
      }))
  }

  return []
}
